import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Params, useParams } from 'react-router-dom';

import {
  type CheckDepositBatch as ApiCheckDepositBatch,
  type ListTransfers as ApiListTransfers,
  type UnifiedTransfer,
  type UpdateCheckDepositBatchRequest,
  transferCheckDepositbatchGetBatch,
  transferCheckDepositbatchGetBatchQueryKey,
  transferCheckDepositbatchUpdateBatch,
  transferSearchTransfers,
  transferSearchTransfersQueryKey,
} from '@column/api';
import { Icon, Input } from '@column/column-ui-kit';
import { Button, DataTable } from '@column/ui';

import { PageActions } from '~/app/Layout';
import { BankAccountCard, Loading, RenderFields, SectionHeader } from '~/components';
import { Empty as TableEmpty, Pagination as TablePagination, tableDefaults } from '~/components/Table';
import { useFormErrorHandler } from '~/hooks/useFormErrorHandler';
import { usePaginationState } from '~/lib/api/hooks/usePaginationState';
import { useNavigate } from '~/lib/navigation';
import { transferTableColumns } from '~/lib/transfers';
import { getTransferURL } from '~/lib/transfers/utils';
import { TransferEntryType } from '~/repositories';
import { TableTimeFormat } from '~/repositories/UserRepository';
import { useTableConfig } from '~/stores/Config';
import { useNotificationStore } from '~/stores/Notification';
import { Inner } from '~/styles';
import { formatDateTime } from '~/util';

import { TransferBatchHeader } from './_components/TransferBatchHeader';

export const PageTransferBatchesView: React.FC = () => {
  const { transferBatchId } = useParams<keyof Params>() as Params;
  const navigate = useNavigate();
  const { handleFormErrors } = useFormErrorHandler();
  const { addSuccessNotification, addDangerNotification } = useNotificationStore();
  const { pageSize, timeFormat, update: updateTableConfig } = useTableConfig('transfers');
  const { cursor, hasPrevious, onNext, onPrevious, pageIndex, setPageSize, ...pagination } = usePaginationState({
    defaultPageSize: pageSize,
  });

  const queryClient = useQueryClient();
  const { data: transferBatch, isLoading: isTransferBatchLoading } = useQuery<ApiCheckDepositBatch>({
    queryKey: transferCheckDepositbatchGetBatchQueryKey({
      path: { check_deposit_batch_id: transferBatchId ?? '' },
    }),
    queryFn: async () => {
      const { data } = await transferCheckDepositbatchGetBatch({
        path: { check_deposit_batch_id: transferBatchId ?? '' },
        throwOnError: true,
      });
      return data as ApiCheckDepositBatch;
    },
    enabled: !!transferBatchId,
    gcTime: 0,
    staleTime: 0,
  });

  const { mutate: updateTransferBatch, isPending: isUpdateTransferBatchPending } = useMutation<
    ApiCheckDepositBatch,
    Error,
    UpdateCheckDepositBatchRequest
  >({
    mutationFn: async (request) => {
      const { data } = await transferCheckDepositbatchUpdateBatch({
        path: { check_deposit_batch_id: transferBatchId ?? '' },
        body: request,
        throwOnError: true,
      });
      return data as ApiCheckDepositBatch;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(
        transferCheckDepositbatchGetBatchQueryKey({
          path: { check_deposit_batch_id: transferBatchId ?? '' },
        }),
        data
      );
      addSuccessNotification({
        content: 'Transfer batch updated',
      });
    },
    onError: (error) => {
      addDangerNotification({
        content: error instanceof Error ? error.message : 'An unknown error occurred',
      });
    },
  });

  const {
    data: transfersData,
    isLoading: isTransfersLoading,
    isFetching: isTransfersFetching,
  } = useQuery<ApiListTransfers>({
    queryKey: transferSearchTransfersQueryKey({
      query: {
        limit: pagination.pageSize,
        starting_after: cursor,
        transfer_batch_id: transferBatchId ?? '',
      },
    }),
    queryFn: async () => {
      const { data } = await transferSearchTransfers({
        query: {
          limit: pagination.pageSize,
          starting_after: cursor,
          transfer_batch_id: transferBatchId ?? '',
        },
        throwOnError: true,
      });
      return data as ApiListTransfers;
    },
    enabled: !!transferBatchId,
    gcTime: 0,
    staleTime: 0,
    placeholderData: keepPreviousData,
  });

  const formMethods = useForm<UpdateCheckDepositBatchRequest>({
    defaultValues: {
      description: transferBatch?.description ?? '',
    },
  });
  // reset() (not setValue) so the loaded value becomes the dirty baseline: the Save button stays disabled
  // until the user actually edits, and re-disables once a save round-trips the new value back.
  useEffect(() => {
    formMethods.reset({ description: transferBatch?.description ?? '' });
  }, [transferBatch?.description, transferBatch?.id]);

  const handleTimeFormatChange = useCallback(
    (value: TableTimeFormat) => {
      updateTableConfig({ timeFormat: value }).catch(() => {
        addDangerNotification({ content: 'Failed to save time format preference' });
      });
    },
    [addDangerNotification, updateTableConfig]
  );

  const columns = useMemo(
    () =>
      transferTableColumns({
        timeFormat,
        onTimeFormatChange: handleTimeFormatChange,
      }),
    [handleTimeFormatChange, timeFormat]
  );

  const transfers = useMemo(() => transfersData?.transfers ?? [], [transfersData]);

  const handleNext = () => {
    if (!transfersData || transfersData.transfers.length === 0) return;
    onNext({ cursor: transfersData.transfers[transfersData.transfers.length - 1].id });
  };

  // Read isDirty during render so react-hook-form subscribes and re-renders (and both Save buttons
  // re-enable) the moment an editable field changes.
  const { isDirty } = formMethods.formState;
  const isSaveDisabled = isUpdateTransferBatchPending || !isDirty;
  const handleSave = formMethods.handleSubmit(
    (data) => updateTransferBatch(data),
    (errors) => handleFormErrors({ errors })
  );
  const saveButton = (
    <Button size="sm" disabled={isSaveDisabled} onClick={handleSave}>
      {isUpdateTransferBatchPending ? 'Saving...' : 'Save Changes'}
    </Button>
  );

  return (
    <>
      {/* Mirror the bottom Save button up in the page header's top-right actions slot. */}
      <PageActions>{saveButton}</PageActions>
      <Loading isLoading={isTransferBatchLoading} fullHeight>
        {transferBatch && (
          <TransferBatchHeader
            amount={transferBatch.total_amount}
            createdAt={transferBatch.created_at}
            currencyCode={transferBatch.currency_code}
            transferBatchId={transferBatch.id}
            totalCount={transferBatch.total_count}
            status={transferBatch.status}
          />
        )}
        <SectionHeader text="Batch Information" border />
        <Inner pt={0} px="var(--page-margin)">
          <form>
            <FormProvider {...formMethods}>
              <RenderFields
                sections={[
                  {
                    variant: 'isCompact',
                    fields: [
                      {
                        id: 'description',
                        label: 'Description',
                        children: ({ onBlur, onChange, value }, { isTouched, error }) => (
                          <Input
                            onChange={onChange}
                            onBlur={onBlur}
                            value={String(value)}
                            hasError={isTouched && !!error}
                          />
                        ),
                      },
                      {
                        id: 'bankAccount',
                        label: 'Bank Account',
                        children: () =>
                          transferBatch?.bank_account_id ? (
                            <BankAccountCard variant="detailed" bankAccountId={transferBatch.bank_account_id} />
                          ) : null,
                      },
                      {
                        id: 'closedAt',
                        label: 'Closes At',
                        children: () =>
                          transferBatch?.closed_at
                            ? formatDateTime(transferBatch.closed_at, { timeZoneFormat: 'local' })
                            : '—',
                      },
                    ],
                  },
                ]}
              />
            </FormProvider>
          </form>
        </Inner>
        <SectionHeader text="Transfers" border />
        <Loading isLoading={isTransfersLoading}>
          <Inner pt={0} px="var(--page-margin)">
            <DataTable<UnifiedTransfer>
              {...tableDefaults}
              columns={columns}
              data={transfers}
              isLoading={isTransfersFetching}
              emptyContent={
                <TableEmpty
                  icon={<Icon.AnimationArrows />}
                  title="No transfers found"
                  description="This batch does not have any transfers yet."
                />
              }
              rowKey={(row) => row.id}
              onRowClick={(row) => navigate(getTransferURL(row.id, row.type as TransferEntryType))}
            />

            {(hasPrevious || transfersData?.has_more) && (
              <TablePagination
                pageSize={pagination.pageSize}
                onPageSizeChange={setPageSize}
                current={pageIndex + 1}
                hasPrevious={hasPrevious}
                hasNext={!!transfersData?.has_more}
                onPrevious={onPrevious}
                onNext={handleNext}
              />
            )}

            <div className="flex justify-end pt-6">{saveButton}</div>
          </Inner>
        </Loading>
      </Loading>
    </>
  );
};
